import { useEffect } from "react";
import { refreshSession } from "../api/auth.js";
import { ApiClientError } from "../api/http.js";
import { notifyUnauthorized } from "./authEvents.js";

const ACTIVITY: ReadonlyArray<keyof WindowEventMap> = ["pointerdown", "keydown", "touchstart"];
export const KEEP_ALIVE_INTERVAL_MS = 120_000;

/**
 * Extends the server session while the user is active. The server caps a login at 12 hours,
 * so a long work session still ends with a re-auth prompt.
 */
export function useSessionKeepAlive(active: boolean): void {
  useEffect((): (() => void) | undefined => {
    if (!active) {
      return undefined;
    }
    let lastRefresh = Date.now();
    let inFlight = false;

    function onActivity(): void {
      if (inFlight || Date.now() - lastRefresh < KEEP_ALIVE_INTERVAL_MS) {
        return;
      }
      inFlight = true;
      void refreshSession().then(
        (): void => {
          lastRefresh = Date.now();
        },
        (err: unknown): void => {
          if (err instanceof ApiClientError && err.status === 401) {
            notifyUnauthorized();
          }
        }
      ).finally((): void => {
        inFlight = false;
      });
    }

    for (const name of ACTIVITY) {
      window.addEventListener(name, onActivity, { passive: true });
    }
    return (): void => {
      for (const name of ACTIVITY) {
        window.removeEventListener(name, onActivity);
      }
    };
  }, [active]);
}
